import { useMemo, useRef, useState, useEffect, type ReactNode } from 'react';
import { Plus, Search, SlidersHorizontal, Trash2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { AddItemButton, FormSectionCard, FormSectionHeader } from '@/components/molecules';
import type { CustomFieldSchema } from '@/types/custom-fields';
import { CustomFieldValueInput } from './CustomFieldValueInput';
import { getInitialCustomFieldDraftValue } from './custom-field-form-utils';

interface CustomFieldsDraftSectionProps {
  open?: boolean;
  onToggle?: () => void;
  schemas: CustomFieldSchema[];
  activeFieldKeys: string[];
  values: Record<string, string>;
  onFieldChange: (fieldKey: string, value: string) => void;
  onActiveFieldKeysChange: (keys: string[]) => void;
  onRemoveField: (fieldKey: string) => void;
  title?: string;
  icon?: LucideIcon;
  alwaysOpen?: boolean;
  allowAddField?: boolean;
  allowRemoveField?: boolean;
  emptyState?: ReactNode;
  /**
   * Optional inline validation errors, keyed by `fieldKey`. When a key has a
   * message, the field renders with an `aria-invalid` ring and the message
   * appears in destructive text below the input.
   *
   * Used by `ContactFormPanel` to surface the client-side validator's output
   * (the same rules the BE enforces) so the user can fix the value BEFORE the
   * round-trip — avoiding the half-success where the contact is created but
   * its custom-field values are rejected.
   */
  fieldErrors?: Record<string, string>;
}

export function CustomFieldsDraftSection({
  open = true,
  onToggle,
  schemas,
  activeFieldKeys,
  values,
  onFieldChange,
  onActiveFieldKeysChange,
  onRemoveField,
  title = 'Custom Fields',
  icon: Icon = SlidersHorizontal,
  alwaysOpen = false,
  allowAddField = true,
  allowRemoveField = true,
  emptyState,
  fieldErrors,
}: CustomFieldsDraftSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const renderedOpen = alwaysOpen || open;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearch(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const availableSchemas = useMemo(
    () =>
      schemas.filter(
        (schema) =>
          !activeFieldKeys.includes(schema.definition.fieldKey) &&
          schema.definition.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [activeFieldKeys, schemas, searchQuery],
  );

  const addFieldControl =
    allowAddField &&
    schemas.some((schema) => !activeFieldKeys.includes(schema.definition.fieldKey)) ? (
      <div ref={searchRef} className="relative">
        {showSearch ? (
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-8 pl-7 pr-2 text-xs"
            />
          </div>
        ) : (
          <AddItemButton label="Add field" onClick={() => setShowSearch(true)} />
        )}
        {showSearch && (
          <div className="absolute left-0 z-50 mt-1 max-h-52 w-56 overflow-auto rounded-md border bg-popover shadow-md">
            {availableSchemas.map((schema) => (
              <button
                key={schema.definition.id}
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={(event) => {
                  event.preventDefault();
                  const fieldKey = schema.definition.fieldKey;
                  onActiveFieldKeysChange([...activeFieldKeys, fieldKey]);
                  if (values[fieldKey] === undefined) {
                    onFieldChange(fieldKey, getInitialCustomFieldDraftValue(schema));
                  }
                  setSearchQuery('');
                }}
              >
                <span>
                  {schema.definition.label}
                  {schema.association.isRequired && (
                    <span className="ml-1 text-xs text-muted-foreground">*</span>
                  )}
                </span>
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))}
            {availableSchemas.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">No fields found</p>
            )}
          </div>
        )}
      </div>
    ) : undefined;

  return (
    <FormSectionCard className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <FormSectionHeader
        icon={Icon}
        title={title}
        open={open}
        onToggle={alwaysOpen ? undefined : onToggle}
        className={renderedOpen ? 'mb-3' : undefined}
      />
      {renderedOpen ? (
        <div className="space-y-3">
          {activeFieldKeys.length > 0 ? (
            <div className="space-y-1">
              {activeFieldKeys.map((fieldKey) => {
                const schema = schemas.find((item) => item.definition.fieldKey === fieldKey);
                if (!schema) return null;
                const value = values[fieldKey] ?? '';
                const fieldId = `cf-${schema.definition.id}`;
                const errorMessage = fieldErrors?.[fieldKey];
                const errorId = errorMessage ? `${fieldId}-error` : undefined;

                return (
                  <div
                    key={fieldKey}
                    className="rounded-md border bg-background px-2 py-2"
                    data-cf-fieldkey={fieldKey}
                  >
                    <div className="flex items-center gap-2">
                      {schema.definition.fieldType === 'CHECKBOX' ? (
                        <div className="w-36 shrink-0" />
                      ) : (
                        <Label htmlFor={fieldId} className="w-36 shrink-0 text-sm">
                          {schema.definition.label}
                          {schema.association.isRequired && (
                            <span className="ml-0.5 text-destructive">*</span>
                          )}
                        </Label>
                      )}

                      <div className="flex-1">
                        <CustomFieldValueInput
                          id={fieldId}
                          schema={schema}
                          value={value}
                          onChange={(next) => onFieldChange(fieldKey, next)}
                          required={schema.association.isRequired}
                          ariaInvalid={Boolean(errorMessage)}
                          ariaDescribedBy={errorId}
                        />
                      </div>

                      {allowRemoveField && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
                          disabled={schema.association.isRequired}
                          onClick={() => onRemoveField(fieldKey)}
                          title={
                            schema.association.isRequired
                              ? 'Required field cannot be removed'
                              : 'Remove field'
                          }
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                    {errorMessage ? (
                      <p
                        id={errorId}
                        className="mt-1 pl-[9.5rem] text-xs text-destructive"
                        role="alert"
                      >
                        {errorMessage}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : emptyState ? (
            emptyState
          ) : null}

          {addFieldControl}

          {activeFieldKeys.length === 0 && !addFieldControl && !emptyState ? (
            <p className="text-sm text-muted-foreground">No custom fields available.</p>
          ) : null}
        </div>
      ) : null}
    </FormSectionCard>
  );
}
